import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'articles' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'articles' },
    meta: { authUser: true },
    children: [
      {
        name: 'articles',
        path: 'articles',
        component: () => import('../views/articles/Articles.vue'),
        children: [
          {
            name: 'createArticle',
            path: 'create_article',
            component: () => import('../views/articles/Article.vue'),
          },
          {
            name: 'editArticle',
            path: 'article/:articleId(.*)',
            props: true,
            component: () => import('../views/articles/Article.vue'),
          }
        ],
      },
      {
        name: 'guides',
        path: 'guides',
        component: () => import('../views/guides/Guides.vue'),
        children: [
          {
            name: 'createGuide',
            path: 'create_guide',
            component: () => import('../views/guides/Guide.vue'),
          },
          {
            name: 'editGuide',
            path: 'guide/:guideId(.*)',
            props: true,
            component: () => import('../views/guides/Guide.vue'),
          }
        ],
      },
      {
        name: 'institutions',
        path: 'institutions',
        component: () => import('../views/institutions/Institutions.vue'),
        children: [
          {
            name: 'createInstitution',
            path: 'create',
            component: () => import('../views/institutions/Institution.vue'),
          },
          {
            name: 'editInstitution',
            path: 'edit/:institutionId(.*)',
            props: true,
            component: () => import('../views/institutions/Institution.vue'),
          },
          {
            name: 'editInstitutionSeo',
            path: 'edit/seo/:institutionId(.*)',
            props: true,
            component: () => import('../views/institutions/InstitutionSeo.vue'),
          },
          {
            name: 'viewInstitution',
            path: 'view/:institutionId(.*)',
            props: true,
            component: () => import('../views/institutions/Institution_view.vue'),
          }
        ],
      },
      {
        name: 'institution_ads',
        path: 'institution-ads',
        component: () => import('../views/institutionAds/InstitutionAds.vue'),
        children: [
          {
            name: 'createInstitutionAd',
            path: 'create',
            component: () => import('../views/institutionAds/InstitutionAd.vue'),
          },
          {
            name: 'editInstitutionAd',
            path: ':institutionAdId(.*)',
            props: true,
            component: () => import('../views/institutionAds/InstitutionAd.vue'),
          },
        ],
      },
      {
        name: 'invoice_offers',
        path: 'invoice-offers',
        component: () => import('../views/invoiceOffers/InvoiceOffers.vue'),
        children: [
          {
            name: 'createInvoiceOffer',
            path: 'create',
            component: () => import('../views/invoiceOffers/InvoiceOffer.vue'),
          },
          {
            name: 'editInvoiceOffer',
            path: ':invoiceOfferId(.*)',
            props: true,
            component: () => import('../views/invoiceOffers/InvoiceOffer.vue'),
          },
        ],
      },
      {
        name: 'workflow',
        path: 'workflow/',
        children: [
          {
            name: 'workflow_reviews',
            path: 'reviews_w',
            component: () => import('../views/workflow/Reviews.vue'),
          },
          {
            name: 'workflow_tutors',
            path: 'tutors_w',
            component: () => import('../views/workflow/Tutors.vue'),
          },
        ],
      },
      {
        name: 'directory',
        path: 'directory',
        children: [
          {
            name: 'programs',
            path: 'programs',
            component: () => import('../views/directory/Programs.vue'),
            children: [
              {
                name: 'createProgram',
                path: 'create',
                component: () => import('../views/directory/Program.vue'),
              },
              {
                name: 'editProgram',
                path: 'edit/:programId(.*)',
                props: true,
                component: () => import('../views/directory/Program.vue'),
              },
            ],
          },
          {
            name: 'countries',
            path: 'countries',
            component: () => import('../views/directory/Countries.vue'),
            children: [
              {
                name: 'country',
                path: ':countryId(.*)/regions',
                props: true,
                component: () => import('../views/directory/Country.vue'),
              },
              {
                name: 'region',
                path: ':countryId(.*)/regions/:regionId(.*)/view',
                props: true,
                component: () => import('../views/directory/Region.vue'),
              },
              {
                name: 'cites',
                path: ':countryId(.*)/cites',
                props: true,
                component: () => import('../views/directory/Cites.vue'),
              },
              {
                name: 'createCity',
                props: true,
                path: ':countryId(.*)/cites/create',
                component: () => import('../views/directory/City.vue'),
              },
              {
                name: 'editCity',
                path: ':countryId(.*)/cites/:cityId(.*)/edit',
                props: true,
                component: () => import('../views/directory/City.vue'),
              },
            ],
          },
          {
            name: 'tags_articles',
            path: 'tags_articles',
            component: () => import('../views/directory/ArticlesTags/ArticlesTags.vue'),
            children: [
              {
                name: 'createArticleTag',
                path: 'create',
                component: () => import('../views/directory/ArticlesTags/ArticleTag.vue'),
              },
              {
                name: 'editArticleTag',
                path: 'edit/:tagId(.*)',
                props: true,
                component: () => import('../views/directory/ArticlesTags/ArticleTag.vue'),
              },
            ],
          },
          {
            name: 'tutor_categories',
            path: 'tutor_categories',
            component: () => import('../views/directory/TutorCategories/TutorCategories.vue'),
            children: [
              {
                name: 'createTutorCategory',
                path: 'create',
                component: () => import('../views/directory/TutorCategories/TutorCategory.vue'),
                props: (route) => ({
                  parentId: route.query.parentId ? Number(route.query.parentId) : 0,
                }),
              },
              {
                name: 'editTutorCategory',
                path: 'edit/:categoryId(.*)',
                props: true,
                component: () => import('../views/directory/TutorCategories/TutorCategory.vue'),
              },
            ],
          },
        ],
      },
      {
        name: 'reviews',
        path: 'reviews',
        component: () => import('../views/reviews/Reviews.vue'),
        children: [
          {
            name: 'reviews_view',
            path: 'view/:reviewId(.*)',
            props: true,
            component: () => import('../views/reviews/Review.vue'),
          },
        ],
      },
      {
        name: 'tutors',
        path: 'tutors',
        component: () => import('../views/tutors/Tutors.vue'),
        children: [
          {
            name: 'tutor',
            path: ':tutorId(.*)',
            props: true,
            component: () => import('../views/tutors/Tutor.vue'),
          },
        ],
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../views/users/Users.vue'),
      },
      {
        name: 'requests',
        path: 'requests',
        component: () => import('../views/requests/Requests.vue'),
        children: [
          {
            name: 'request',
            path: ':requestId(.*)',
            props: true,
            component: () => import('../views/requests/Request.vue'),
          },
        ],
      },
      {
        name: 'tutor_requests',
        path: 'tutor_requests',
        component: () => import('../views/tutorRequests/TutorRequests.vue'),
        children: [
          {
            name: 'tutor_request',
            path: ':sendId(.*)',
            props: true,
            component: () => import('../views/tutorRequests/TutorRequest.vue'),
          },
        ],
      },
      {
        name: 'requests_partnerships',
        path: 'requests_partnerships',
        component: () => import('../views/requestsPartnerships/RequestsPartnerships.vue'),
      },
      {
        name: 'catalog',
        path: 'catalog/',
        component: () => import('../views/catalog/Catalog.vue'),
        children: [
          {
            name: 'catalog_orgtype',
            path: ':orgtypeId',
            props: true,
            component: () => import('../views/catalog/Orgtype.vue'),
            children: [
              {
                name: 'catalog_orgtype_country',
                path: ':countryId',
                props: true,
                component: () => import('../views/catalog/Country.vue'),
                children: [
                  {
                    name: 'catalog_orgtype_country_region',
                    path: 'region/:regionId',
                    props: true,
                    component: () => import('../views/catalog/Region.vue'),
                    children: [
                      {
                        name: 'catalog_orgtype_country_region_city',
                        path: ':cityId',
                        props: true,
                        component: () => import('../views/catalog/City.vue'),
                      },
                    ]
                  },
                  {
                    name: 'catalog_orgtype_country_city',
                    path: 'city/:cityId',
                    props: true,
                    component: () => import('../views/catalog/City.vue'),
                  }
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'online_consultations',
        path: 'online_consultations',
        children: [
          {
            name: 'schedule_management',
            path: 'schedule_management',
            props: true,
            component: () => import('../views/requests/ScheduleManagement.vue'),
          },
          {
            name: 'calendar',
            path: 'calendar',
            props: true,
            component: () => import('../views/requests/Calendar.vue'),
          },
        ],
      },
      {
        name: 'application_forms',
        path: 'application_forms',
        component: () => import('../views/applicationForms/ApplicationForms.vue'),
        children: [
          {
            name: 'application_form_view',
            path: ':applicationFormId(.*)',
            props: true,
            component: () => import('../views/applicationForms/ApplicationForm.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    name: 'auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

export default function (store: any) {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      }
      // For some reason using documentation example doesn't scroll on page navigation.
      if (to.hash) {
        return { el: to.hash, behavior: 'smooth' }
      } else {
        window.scrollTo(0, 0)
      }
    },
    routes,
  })

  router.beforeEach(async (to, from, next) => {
    if (!store.userStore.loaded_data) {
      await store.userStore.auth()
    }

    if (to.meta.authUser !== undefined && to.meta.authUser && store.userStore.is_auth) {
      if (to.meta.newUser !== undefined && to.meta.newUser && store.userStore.user.status !== 'new') {
        next('/auth/login')
      }
    }
    if (to.meta.authUser !== undefined && to.meta.authUser && store.userStore.is_auth) {
    } else if (to.meta.authUser !== undefined && to.meta.authUser && !store.userStore.is_auth) {
      next('/auth/login')
    }
    // if (to.meta.env !== undefined && !to.meta.env.includes(import.meta.env.VITE_ENV))
    // {
    //     next('/')
    // }
    next()
  })

  return router
}

// export default router
